import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { apiClient, ApiError } from "@/api/client";
import type { FocusProfileResponse } from "@/api/types";

/**
 * Shared `/me/focus-profile` cache (PRD §6.7 PE-7.1).
 *
 * Today's Focus card and the "How focus grows" panel both read the profile, and
 * the session overlay produces a new one the moment a block closes. Fetching
 * per-component would leave Today claiming the old capacity after a session —
 * a student finishing a 25-minute block would watch their number not move — so
 * the response lives here and the overlay writes through via {@link adopt}.
 *
 * Mirrors `stores/home.ts`: `load()` is fetch-once, `refresh()` forces a
 * refetch, concurrent callers share one request.
 */
export const useFocusStore = defineStore("focus", () => {
  const data = ref<FocusProfileResponse | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);

  let inflight: Promise<void> | null = null;

  const loaded = computed(() => data.value !== null);

  /**
   * Null below `MIN_BLOCKS_FOR_CAPACITY` completed blocks. Null is *unmeasured*,
   * never zero — no surface may substitute a 0 for it (PRD §8.4).
   */
  const capacityMinutes = computed<number | null>(
    () => data.value?.capacity_minutes ?? null,
  );
  const hasCapacity = computed(() => capacityMinutes.value !== null);

  function fetchOnce(): Promise<void> {
    if (!inflight) {
      inflight = (async () => {
        loading.value = true;
        error.value = null;
        try {
          data.value = await apiClient.getFocusProfile();
        } catch (e) {
          error.value =
            e instanceof ApiError
              ? `API error ${e.status}`
              : "Failed to load your focus profile";
        } finally {
          loading.value = false;
        }
      })().finally(() => {
        inflight = null;
      });
    }
    return inflight;
  }

  /** Fetch unless already loaded. Concurrent callers share one request. */
  async function load(): Promise<void> {
    if (data.value !== null) return;
    await fetchOnce();
  }

  /** Force a refetch — call after changing the focus goal. */
  async function refresh(): Promise<void> {
    await fetchOnce();
  }

  /**
   * Write through a profile the caller already fetched. The session overlay
   * refetches the profile itself after a block closes (it needs the before/after
   * pair synchronously to phrase the completion outcome), so it hands the result
   * over rather than making Today spend a second request on the same bytes.
   */
  function adopt(profile: FocusProfileResponse): void {
    data.value = profile;
    error.value = null;
  }

  /** Drop cached state on logout so the next user never sees the last one's focus. */
  function reset(): void {
    data.value = null;
    error.value = null;
  }

  return {
    data,
    loaded,
    loading,
    error,
    capacityMinutes,
    hasCapacity,
    load,
    refresh,
    adopt,
    reset,
  };
});
